import VerifiedIcon from '@mui/icons-material/Verified';
import { Box, Chip, Grid, Link, Tooltip, Typography } from '@mui/material';
import StatusChip from './StatusChip';
import { COMMON_COLORS, CONFIDENCE_COLORS, FONT_WEIGHTS } from './constants';

export interface FieldDisplayProps {
  label: string;
  value?: string | number | null | undefined;

  // Grid configuration
  gridSize?: { xs: number; md?: number; lg?: number };

  // Display variants
  variant?: 'default' | 'boxed' | 'simple' | 'prominent';

  // Typography customization
  labelVariant?: 'caption' | 'body2';
  valueVariant?: 'body1' | 'body2';
  fontWeight?: 'normal' | 'medium' | 'semibold' | 'bold';

  // Visual customization
  showEmptyState?: boolean;
  backgroundColor?: string;
  borderColor?: string;

  // Links and actions
  href?: string;
  onClick?: () => void;
  startIcon?: React.ReactNode;
  endIcon?: React.ReactNode;

  // Special formatting
  formatter?: (value: any) => string;
  confidence?: number;

  // Status styling
  status?: 'success' | 'warning' | 'error' | 'info';

  // Chip rendering
  renderAsChip?: boolean;
  chipVariant?: 'filled' | 'outlined';
  chipColor?: string;

  // Editing support
  isEditing?: boolean;
  editComponent?: React.ReactNode;
}

const FieldDisplay: React.FC<FieldDisplayProps> = ({
  label,
  value,
  gridSize = { xs: 12, md: 4 },
  variant = 'default',
  labelVariant = 'caption',
  valueVariant = 'body2',
  fontWeight = 'normal',
  showEmptyState = true,
  backgroundColor,
  borderColor,
  href,
  onClick,
  startIcon,
  endIcon,
  formatter,
  confidence,
  status,
  renderAsChip = false,
  chipVariant = 'filled',
  chipColor,
  isEditing = false,
  editComponent,
}) => {
  const hasValue = value !== null && value !== undefined && value !== '' && value !== 'Unknown';
  const displayValue = hasValue
    ? formatter
      ? formatter(value)
      : typeof value === 'object'
      ? JSON.stringify(value, null, 2)
      : String(value)
    : showEmptyState
    ? '—'
    : '';

  const getLabelColor = () => {
    return 'text.secondary';
  };

  const getValueColor = () => {
    if (!hasValue) {
      return 'text.disabled';
    }
    if (status === 'success') {
      return 'success.main';
    }
    if (status === 'warning') {
      return 'warning.main';
    }
    if (status === 'error') {
      return 'error.main';
    }
    if (status === 'info') {
      return 'info.main';
    }
    return 'text.primary';
  };

  const getFontWeight = () => {
    switch (fontWeight) {
      case 'medium':
        return FONT_WEIGHTS.MEDIUM;
      case 'semibold':
        return FONT_WEIGHTS.SEMIBOLD;
      case 'bold':
        return '700';
      default:
        return FONT_WEIGHTS.LIGHT;
    }
  };

  const getVariantStyles = () => {
    switch (variant) {
      case 'boxed':
        return {
          container: {
            p: 1.5,
            border: 1,
            borderColor: borderColor || 'divider',
            borderRadius: 1,
            bgcolor: backgroundColor || 'background.paper',
            minHeight: 64,
            display: 'flex',
            flexDirection: 'column',
          } as const,
          spacing: {} as const,
        };
      case 'simple':
        return {
          container: {
            minHeight: 48,
          } as const,
          spacing: { mb: 0.5 } as const,
        };
      case 'prominent':
        return {
          container: {
            p: 1,
            bgcolor: backgroundColor || 'grey.50',
            borderRadius: 1,
            minHeight: 56,
          } as const,
          spacing: { mb: 0.5 } as const,
        };
      case 'default':
      default:
        return {
          container: {
            display: 'flex',
            alignItems: 'center',
            minHeight: 32,
            p: 1,
            bgcolor: backgroundColor || 'grey.50',
            borderRadius: 1,
          } as const,
          spacing: { mb: 0.5 } as const,
        };
    }
  };

  const variantStyles = getVariantStyles();

  const renderValue = () => {
    if (isEditing && editComponent) {
      return editComponent;
    }

    if (renderAsChip && hasValue) {
      return (
        <StatusChip
          label={displayValue}
          color={chipColor || getValueColor()}
          fontWeight={getFontWeight()}
          variant={chipVariant}
          size='small'
        />
      );
    }

    const isLongId =
      typeof value === 'string' && value.length > 40 && label.toLowerCase().includes('id');

    const valueElement = (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, width: '100%' }}>
        {startIcon}
        <Typography
          variant={valueVariant}
          color={getValueColor()}
          fontWeight={getFontWeight()}
          component={href || onClick ? 'span' : 'div'}
          sx={{
            wordBreak: isLongId ? 'break-all' : 'break-word',
            fontFamily:
              typeof value === 'string' && (value.includes('id') || value.includes('number'))
                ? 'monospace'
                : 'inherit',
            flex: variant === 'boxed' ? 1 : 'none',
            whiteSpace: typeof value === 'object' ? 'pre-wrap' : 'normal',
            overflow: isLongId ? 'hidden' : 'visible',
            textOverflow: isLongId ? 'ellipsis' : 'clip',
            maxWidth: '100%',
          }}>
          {displayValue}
        </Typography>
        {endIcon}
      </Box>
    );

    const finalElement = isLongId ? (
      <Tooltip title={displayValue} placement='top'>
        {valueElement}
      </Tooltip>
    ) : (
      valueElement
    );

    if (href) {
      return (
        <Link href={href} target='_blank' rel='noopener noreferrer' sx={{ textDecoration: 'none' }}>
          {finalElement}
        </Link>
      );
    }

    if (onClick) {
      return (
        <Box
          component='button'
          onClick={onClick}
          sx={{
            background: 'none',
            border: 'none',
            cursor: 'pointer',
            p: 0,
            textAlign: 'left',
            '&:hover': { opacity: 0.7 },
          }}>
          {finalElement}
        </Box>
      );
    }

    return finalElement;
  };

  const renderConfidenceChip = () => {
    if (confidence === undefined || !hasValue) {
      return null;
    }

    const getConfidenceColor = () => {
      if (confidence >= 80) {
        return CONFIDENCE_COLORS.HIGH;
      }
      if (confidence >= 60) {
        return CONFIDENCE_COLORS.MEDIUM;
      }
      return CONFIDENCE_COLORS.LOW;
    };

    return (
      <Tooltip title={`LLM confidence: ${confidence}%`}>
        <Chip
          icon={<VerifiedIcon sx={{ fontSize: '12px !important' }} />}
          label={`${confidence}%`}
          size='small'
          sx={{
            height: 18,
            fontSize: '0.65rem',
            backgroundColor: getConfidenceColor(),
            color: 'white',
            '& .MuiChip-icon': {
              color: `${COMMON_COLORS.WHITE} !important`,
            },
          }}
        />
      </Tooltip>
    );
  };

  return (
    <Grid item {...gridSize}>
      {variant === 'boxed' ? (
        <Box
          sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.5 }}>
          <Typography
            variant={labelVariant}
            color={getLabelColor()}
            fontWeight={FONT_WEIGHTS.MEDIUM}
            sx={{ lineHeight: 1.2 }}>
            {label}
          </Typography>
          {renderConfidenceChip()}
        </Box>
      ) : (
        <Typography
          variant={labelVariant}
          color={getLabelColor()}
          display='block'
          sx={variantStyles.spacing}
          fontWeight={FONT_WEIGHTS.MEDIUM}>
          {label}
        </Typography>
      )}
      <Box sx={{ ...variantStyles.container }}>{renderValue()}</Box>
    </Grid>
  );
};

export default FieldDisplay;
